// TimeCardExpenses — the day's expenses, filed from the time card (Matt, 2026-10-25).
//
// An expense is filed the day it happens, from the same card the hours go on, so it is never
// found at billing time and never arrives without an owner (65 mileage claims once did). The
// filer is always the person signed in: the server stamps it.
//
// Two questions decide what happens after approval, so they come first:
//   Who paid?  "I paid" → the firm owes them (Payor = Employee).
//              "Company card or account" → nothing to pay back (Payor = Alder James).
//   The project, which the day's own time entries offer first.
// Mileage asks only for the miles; the server prices it at Core's rate.
//
// Reviewed in Time Review by whoever reviews this person's time (docs/EXPENSE_REPORTS.md).
// Follows the time card's day through the `timecard-day` event, like TimeCardSuggestions.
import { useEffect, useState, useCallback, useRef } from 'react'
import { listExpenseReports, saveExpenseReport, uploadReceipt, expenseItemCodes, isMileage } from '../services/expenseReports.js'
import { getActiveProjects } from '../services/commands.js'
import { getEntryPhases } from '../services/timecards.js'
import { alertDialog } from './Dialog.jsx'
import SearchSelect from './SearchSelect.jsx'

const font = '"DM Sans", sans-serif'
const mono = '"DM Mono", monospace'
const S = {
  wrap: { margin: '14px 0 0', padding: '12px 14px', border: '0.5px solid var(--aj-border)', borderRadius: 10, background: 'var(--aj-bg-surface)', fontFamily: font, fontSize: 13, color: 'var(--aj-text)' },
  head: { display: 'flex', alignItems: 'baseline', gap: 10 },
  title: { fontFamily: '"Montserrat", sans-serif', fontWeight: 700, fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase' },
  muted: { color: 'var(--aj-text-muted)', fontSize: 12 },
  label: { display: 'block', fontFamily: mono, fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--aj-text-muted)', marginBottom: 4 },
  input: { width: '100%', boxSizing: 'border-box', padding: '6px 8px', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, background: 'var(--aj-bg-page)', color: 'var(--aj-text)', fontFamily: font, fontSize: 13 },
  btn: { padding: '5px 12px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-surface)', color: 'var(--aj-text)', fontFamily: font, fontSize: 12, cursor: 'pointer' },
  primary: { background: '#07272D', color: '#fff', border: '0.5px solid #07272D' },
  row: { display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto auto', gap: 10, alignItems: 'center', padding: '7px 0', borderTop: '0.5px solid var(--aj-border)', fontSize: 12 },
  chip: { padding: '4px 10px', borderRadius: 20, border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-page)', color: 'var(--aj-text)', fontFamily: font, fontSize: 12, cursor: 'pointer' },
  chipOn: { background: '#07272D', color: '#fff', border: '0.5px solid #07272D' },
}
const money = (n) => n == null ? '—' : '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const shortItem = (c) => String(c || '').replace(/^EX-\s*/, '')
function todayStr() {
  const d = new Date()
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
}
const STATUS_LABEL = { 'Submitted': 'Waiting for review', 'Approved': 'Approved', 'Rejected': 'Sent back', 'Recorded in BQE Core': 'In Core' }

function PaidTile({ on, title, sub, onClick }) {
  return (
    <button type="button" onClick={onClick} style={{
      textAlign: 'left', padding: '10px 12px', borderRadius: 8, cursor: 'pointer', fontFamily: font,
      border: on ? '1.5px solid #07272D' : '0.5px solid var(--aj-border-mid)',
      background: on ? 'var(--aj-bg-subtle)' : 'var(--aj-bg-page)', color: 'var(--aj-text)',
    }}>
      <div style={{ fontWeight: 600, fontSize: 13 }}>{title}</div>
      <div style={{ fontSize: 12, color: 'var(--aj-text-muted)', marginTop: 2 }}>{sub}</div>
    </button>
  )
}

function ExpenseForm({ day, dayProjects, projects, phases, phaseByProject, rates, onDone, onCancel }) {
  const [payor, setPayor] = useState('Employee')
  const [itemCode, setItemCode] = useState('EX- Mileage Exp')
  const [projectId, setProjectId] = useState(dayProjects[0] ? dayProjects[0].id : '')
  const [overhead, setOverhead] = useState(false)
  const [qty, setQty] = useState('')
  const [payee, setPayee] = useState('')
  const [memo, setMemo] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [phaseId, setPhaseId] = useState('')
  const fileRef = useRef(null)
  // Core bills by PHASE. The choices are the phases open for time on the chosen project (or the
  // overhead project's); the default is the phase this day's time went to on that project.
  const projName = overhead ? '' : ((projects.find(p => p.id === projectId) || {}).name || '')
  const phaseOptions = phases.filter(ph => overhead ? /overhead/i.test(ph.name) : (projName && ph.name.toLowerCase().includes(projName.toLowerCase())))
  useEffect(() => {
    if (phaseOptions.some(ph => ph.id === phaseId)) return
    const dayPh = projName ? (phaseByProject || {})[projName] : null
    setPhaseId(phaseOptions.some(ph => ph.id === dayPh) ? dayPh : (phaseOptions[0] ? phaseOptions[0].id : ''))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, overhead, phases.length, projName])
  const phaseLabel = (n) => { const parts = String(n).split(' - '); return parts.length > 2 ? parts.slice(2).join(' - ') : n }
  const mileage = isMileage(itemCode)
  const rate = rates[itemCode] || null
  const amount = mileage ? (Number(qty) > 0 && rate ? Number(qty) * rate : null) : (Number(qty) > 0 ? Number(qty) : null)

  async function save() {
    setErr('')
    if (!(Number(qty) > 0)) { setErr(mileage ? 'Enter the miles driven.' : 'Enter the amount.'); return }
    if (!overhead && !projectId) { setErr('Pick the project, or choose Overhead.'); return }
    if (!phaseId) { setErr('Pick the phase. It decides which invoice this lands on.'); return }
    if (!memo.trim()) { setErr('Say what it was for, so your reviewer can approve it.'); return }
    setBusy(true)
    try {
      const res = await saveExpenseReport({
        date: day, payor, itemCode, payee, memo,
        expenseType: overhead ? 'Overhead' : 'Project',
        projectId: overhead ? null : projectId,
        phaseId,
        units: mileage ? Number(qty) : null,
        unitCost: mileage ? '' : Number(qty),
      })
      const file = fileRef.current && fileRef.current.files && fileRef.current.files[0]
      if (file) {
        try { await uploadReceipt(res.report.id, file) }
        catch { await alertDialog({ title: 'Filed, but the receipt did not upload', message: 'The expense was filed. Add the receipt from Expenses in the menu.' }) }
      }
      onDone(mileage ? `${qty} mi filed. ${payor === 'Employee' ? 'Goes to your reviewer, then into your reimbursement.' : 'Goes to your reviewer.'}`
        : `${money(Number(qty))} filed. ${payor === 'Employee' ? 'Goes to your reviewer, then into your reimbursement.' : 'Goes to your reviewer. Nothing to pay back.'}`)
    } catch (e) { setErr(e.message || 'Could not file it.') }
    finally { setBusy(false) }
  }

  const others = projects.filter(p => !dayProjects.some(d => d.id === p.id))
  return (
    <div style={{ marginTop: 10, paddingTop: 10, borderTop: '0.5px solid var(--aj-border)' }}>
      <label style={S.label}>Who paid?</label>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
        <PaidTile on={payor === 'Employee'} title="I paid" sub="Pay me back" onClick={() => setPayor('Employee')} />
        <PaidTile on={payor === 'Alder James'} title="Company card or account" sub="Record it, nothing to pay back"
          onClick={() => { setPayor('Alder James'); if (isMileage(itemCode)) setItemCode('EX- Permit & Governmental Agency Costs') }} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.4fr) minmax(0,1fr)', gap: 10, marginBottom: 12 }}>
        <div>
          <label style={S.label}>What</label>
          <select value={itemCode} onChange={e => { setItemCode(e.target.value); setQty('') }} style={S.input}>
            {expenseItemCodes().map(c => <option key={c} value={c}>{shortItem(c)}</option>)}
          </select>
        </div>
        <div>
          <label style={S.label}>{mileage ? 'Miles' : 'Amount ($)'}</label>
          <input type="number" min="0" step={mileage ? '1' : '0.01'} value={qty} onChange={e => setQty(e.target.value)}
            placeholder={mileage ? '38' : '0.00'} style={{ ...S.input, fontFamily: mono }} />
          {mileage && <div style={{ ...S.muted, marginTop: 3, fontFamily: mono }}>{rate ? `at $${rate}/mi · ${money(amount)}` : 'priced at the firm mileage rate'}</div>}
        </div>
      </div>

      <label style={S.label}>Project</label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
        {dayProjects.map(p => (
          <button key={p.id} type="button" onClick={() => { setOverhead(false); setProjectId(p.id) }}
            style={{ ...S.chip, ...(!overhead && projectId === p.id ? S.chipOn : null) }}>{p.label}</button>
        ))}
        <button type="button" onClick={() => setOverhead(true)} style={{ ...S.chip, ...(overhead ? S.chipOn : null) }}>Overhead</button>
      </div>
      {!overhead && (
        <SearchSelect value={dayProjects.some(d => d.id === projectId) ? '' : projectId}
          options={others.map(p => ({ value: p.id, label: p.label }))}
          onChange={v => { setOverhead(false); setProjectId(v) }}
          placeholder={dayProjects.length ? 'Another project…' : 'Pick a project…'} style={{ ...S.input, marginBottom: 12 }} />
      )}
      {overhead && <div style={{ ...S.muted, marginBottom: 12 }}>Overhead is a firm cost. It is never billed to a client.</div>}
      <label style={S.label}>Phase</label>
      {phaseOptions.length ? (
        <select value={phaseId} onChange={e => setPhaseId(e.target.value)} style={{ ...S.input, marginBottom: 12 }}>
          {phaseOptions.map(ph => <option key={ph.id} value={ph.id}>{phaseLabel(ph.name)}</option>)}
        </select>
      ) : (
        <div style={{ ...S.muted, marginBottom: 12 }}>{phases.length ? (overhead || projectId ? 'No phase of this project is open for time. Ask your manager to open one.' : 'Pick the project first.') : 'Loading phases…'}</div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1.6fr)', gap: 10, marginBottom: 10 }}>
        <div>
          <label style={S.label}>Paid to</label>
          <input value={payee} onChange={e => setPayee(e.target.value)} placeholder={mileage ? 'not needed' : 'City of Laguna Beach'} style={S.input} />
        </div>
        <div>
          <label style={S.label}>What it was for</label>
          <input value={memo} onChange={e => setMemo(e.target.value)} placeholder={mileage ? 'Site visit to verify staking' : 'Plan check fee, paid at the counter'} style={S.input} />
        </div>
      </div>
      {!mileage && (
        <div style={{ marginBottom: 10 }}>
          <label style={S.label}>Receipt</label>
          <input ref={fileRef} type="file" accept="image/*,application/pdf" style={{ ...S.input, padding: '4px 6px', fontSize: 12 }} />
        </div>
      )}
      {err && <div style={{ color: '#c0392b', fontSize: 12, marginBottom: 8 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button type="button" onClick={save} disabled={busy} style={{ ...S.btn, ...S.primary, opacity: busy ? 0.6 : 1 }}>
          {busy ? 'Filing…' : (payor === 'Employee' ? 'File it, pay me back' : 'File it')}
        </button>
        <button type="button" onClick={onCancel} style={{ ...S.btn, border: 'none', background: 'none', color: 'var(--aj-text-muted)' }}>Cancel</button>
      </div>
    </div>
  )
}

export default function TimeCardExpenses({ initialDate }) {
  const [day, setDay] = useState(initialDate || todayStr())
  const [projectNames, setProjectNames] = useState([])
  const [phaseByProject, setPhaseByProject] = useState({})
  const [phases, setPhases] = useState([])
  const [mine, setMine] = useState(null)
  const [rates, setRates] = useState({})
  const [error, setError] = useState('')
  const [projects, setProjects] = useState([])
  const [adding, setAdding] = useState(false)
  const [done, setDone] = useState('')

  useEffect(() => {
    function onDay(e) {
      if (!e || !e.detail || !e.detail.date) return
      setDay(prev => { if (prev !== e.detail.date) { setAdding(false); setDone('') } return e.detail.date })
      setProjectNames(e.detail.projectNames || [])
      setPhaseByProject(e.detail.phaseByProject || {})
    }
    window.addEventListener('timecard-day', onDay)
    return () => window.removeEventListener('timecard-day', onDay)
  }, [])

  const load = useCallback(() => {
    listExpenseReports('mine').then(d => { setMine(d.reports || []); setRates(d.rates || {}); setError('') })
      .catch(e => { setMine([]); setError(e.message || 'Could not load your expenses') })
  }, [])
  useEffect(() => { load() }, [load])
  useEffect(() => {
    getActiveProjects().then(recs => setProjects((recs || []).map(r => {
      const num = r.fields['Project Number'] || '', name = r.fields['Project Name'] || ''
      return { id: r.id, name, label: (num + ' ' + name).trim() }
    }).filter(p => !/overhead/i.test(p.name)).sort((a, b) => a.label.localeCompare(b.label)))).catch(() => setProjects([]))
  }, [])

  useEffect(() => { getEntryPhases().then(list => setPhases((list || []).map(p => ({ id: p.id, name: p.name })))).catch(() => setPhases([])) }, [])

  // The day's time entries name their projects by name; match them to the project list.
  // The firm's own overhead project is the Overhead choice, not a project to bill.
  const dayProjects = projectNames.filter(n => !/overhead/i.test(n)).map(n => {
    const key = String(n).toLowerCase()
    return projects.find(p => p.name.toLowerCase() === key || p.label.toLowerCase() === key)
  }).filter(Boolean)

  const today = (mine || []).filter(x => (x.date || '').slice(0, 10) === day)
  const sentBack = (mine || []).filter(x => x.status === 'Rejected')

  return (
    <div style={S.wrap}>
      <div style={S.head}>
        <span style={S.title}>Expenses</span>
        <span style={S.muted}>{mine === null ? 'loading…' : today.length ? `${today.length} this day · ${money(today.reduce((a, x) => a + (Number(x.amount) || 0), 0))}` : 'none this day'}</span>
        {!adding && <button type="button" onClick={() => { setAdding(true); setDone('') }} style={{ ...S.btn, marginLeft: 'auto' }}>+ Add expense</button>}
      </div>
      {error && <div style={{ color: '#c0392b', fontSize: 12, marginTop: 6 }}>⚠ {error}</div>}
      {sentBack.length > 0 && (
        <div style={{ marginTop: 8, padding: '6px 10px', borderRadius: 6, border: '0.5px solid #b7791f', color: '#b7791f', fontSize: 12 }}>
          {sentBack.length} sent back to you to fix. Open Expenses in the menu to fix and resubmit.
          {sentBack.slice(0, 3).map(x => <div key={x.id} style={{ marginTop: 3 }}>{(x.date || '').slice(0, 10)} · {shortItem(x.itemCode)} · {x.workflowMemo}</div>)}
        </div>
      )}
      {today.map(x => (
        <div key={x.id} style={S.row}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600 }}>{shortItem(x.itemCode)} · {x.projectName || (x.expenseType === 'Overhead' ? 'Overhead' : '—')}</div>
            <div style={{ ...S.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{[x.payor === 'Employee' ? 'Pay back' : 'Company paid', x.payee, x.memo].filter(Boolean).join(' · ')}</div>
          </div>
          <span style={{ fontFamily: mono }}>{isMileage(x.itemCode) && x.units ? `${x.units} mi · ` : ''}{money(x.amount)}</span>
          <span style={{ ...S.muted, whiteSpace: 'nowrap' }}>{x.core && x.core.paidAt ? 'Paid back' : (STATUS_LABEL[x.status] || x.status)}</span>
        </div>
      ))}
      {done && !adding && <div style={{ marginTop: 8, fontSize: 12, color: 'var(--aj-text-mid)' }}>{done}</div>}
      {adding && (
        <ExpenseForm day={day} dayProjects={dayProjects} projects={projects} phases={phases} phaseByProject={phaseByProject} rates={rates}
          onCancel={() => setAdding(false)}
          onDone={(msg) => { setAdding(false); setDone(msg); load() }} />
      )}
    </div>
  )
}
